import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Typography,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  Divider,
  Paper,
  IconButton,
  useMediaQuery,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import api from '../api/client';

// Puste/generyczne wartości domyślne — żadnych sezonowych/przykładowych
// treści ("Jesień", konkretne marki nagród itd.). Rzeczywiste dane
// (opis, nagrody, kategoria) są dociągane z kampanii przy pierwszym
// wejściu (patrz useEffect poniżej), reszta zostaje pusta do wypełnienia.
const INITIAL_CONFIG = {
  campaignTitle: '',
  subtitle: '',
  companyName: '',
  heroColor: '#6D55F7',
  accentColor: '#9DE94B',
  code: '',
  description: '',
  steps: [
    { title: 'Pobierz aplikację', description: 'Pobierz aplikację na telefon i zaloguj się firmowym adresem e-mail.' },
    { title: 'Dołącz do wyzwania', description: 'Wpisz kod dołączenia i potwierdź udział.' },
    { title: 'Połącz aplikacje zdrowotne', description: 'Połącz Apple Health lub Health Connect, aby Twoja aktywność liczyła się automatycznie.' },
  ],
  charityEnabled: false,
  charityTitle: '',
  charityDescription: '',
  charityName: '',
  rules: [],
  rewards: [],
  published: false,
};

function slugifyTitle(title) {
  return (title || 'kampania')
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 24) || 'kampania';
}

function generateLandingCode(title) {
  const suffix = Math.random().toString(36).slice(2, 6);
  return `${slugifyTitle(title)}-${suffix}`;
}

function Section({ title, children }) {
  const theme = useTheme();
  return (
    <Paper
      sx={{
        p: { xs: 2.5, sm: 3 },
        mb: 3,
        borderRadius: `${theme.tokens?.primitive?.radius?.card ?? 20}px`,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: 'none',
      }}
    >
      <Typography variant="subtitle1" sx={{ fontFamily: theme.typography.h1.fontFamily, fontWeight: 700, mb: 2.5 }}>
        {title}
      </Typography>
      {children}
    </Paper>
  );
}

function LandingConfigurator() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [config, setConfig] = useState(INITIAL_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [requiresMobileApp, setRequiresMobileApp] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    api.get(`/api/campaigns/${id}/landing`)
      .then((data) => {
        if (data && !data.error) {
          // Parse config JSON from API
          let parsed = {};
          try { parsed = typeof data.config === 'string' ? JSON.parse(data.config) : (data.config || {}); } catch { parsed = {}; }
          setRequiresMobileApp(data.requires_mobile_app === 1 || data.requires_mobile_app === true);

          // Gdy strona jeszcze nie istnieje (pierwsze wejście), podstawiamy
          // sensowne wartości startowe z samej kampanii (opis, nagrody,
          // charytatywność) — a nie generyczne/sezonowe treści przykładowe.
          let campaignRewards = [];
          try {
            const raw = typeof data.campaign_rewards === 'string' ? JSON.parse(data.campaign_rewards) : data.campaign_rewards;
            if (Array.isArray(raw)) campaignRewards = raw.map((r) => ({ place: r.place, prize: r.prize }));
          } catch { /* ignore */ }
          let campaignCharity = {};
          try {
            campaignCharity = typeof data.campaign_charity === 'string' ? JSON.parse(data.campaign_charity) : (data.campaign_charity || {});
          } catch { /* ignore */ }

          setConfig((prev) => ({
            ...prev,
            campaignTitle: parsed.campaignTitle || parsed.title || data.campaign_title || prev.campaignTitle,
            subtitle: parsed.subtitle || prev.subtitle,
            companyName: parsed.companyName || prev.companyName,
            heroColor: parsed.heroColor || prev.heroColor,
            accentColor: parsed.accentColor || prev.accentColor,
            code: data.code || prev.code || generateLandingCode(parsed.campaignTitle || data.campaign_title),
            description: parsed.description || data.campaign_description || prev.description,
            steps: parsed.steps || prev.steps,
            charityEnabled: parsed.charityEnabled ?? !!campaignCharity.enabled ?? prev.charityEnabled,
            charityTitle: parsed.charityTitle || prev.charityTitle,
            charityDescription: parsed.charityDescription || prev.charityDescription,
            charityName: parsed.charityName || campaignCharity.name || prev.charityName,
            rules: parsed.rules || prev.rules,
            rewards: (parsed.rewards && parsed.rewards.length ? parsed.rewards : campaignRewards.length ? campaignRewards : prev.rewards),
            published: data.published === 1 || data.published === true,
          }));
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { code, published, ...rest } = config;
      const trimmedCode = (code || '').trim();
      if (published && !trimmedCode) {
        setSnackbar({ open: true, message: 'Ustaw kod dołączenia przed opublikowaniem strony.', severity: 'error' });
        setSaving(false);
        return;
      }
      await api.put(`/api/campaigns/${id}/landing`, {
        code: trimmedCode,
        published,
        config: rest,
      });
      setSnackbar({ open: true, message: 'Konfiguracja zapisana!', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: `Błąd zapisu: ${err.body?.error || err.message}`, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(landingUrl).then(() => {
      setSnackbar({ open: true, message: 'Link skopiowany!', severity: 'success' });
    });
  };

  const handleChange = (field) => (e) => {
    setConfig((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSwitch = (field) => (e) => {
    setConfig((prev) => ({ ...prev, [field]: e.target.checked }));
  };

  const handleStepChange = (idx, field) => (e) => {
    setConfig((prev) => {
      const steps = [...prev.steps];
      steps[idx] = { ...steps[idx], [field]: e.target.value };
      return { ...prev, steps };
    });
  };

  const handleRuleChange = (idx, field) => (e) => {
    setConfig((prev) => {
      const rules = [...prev.rules];
      rules[idx] = { ...rules[idx], [field]: e.target.value };
      return { ...prev, rules };
    });
  };

  const handleRewardChange = (idx, field) => (e) => {
    setConfig((prev) => {
      const rewards = [...prev.rewards];
      rewards[idx] = { ...rewards[idx], [field]: e.target.value };
      return { ...prev, rewards };
    });
  };

  const addStep = () => {
    setConfig((prev) => ({
      ...prev,
      steps: [...prev.steps, { title: '', description: '' }],
    }));
  };

  const removeStep = (idx) => {
    setConfig((prev) => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== idx),
    }));
  };

  const addReward = () => {
    setConfig((prev) => ({
      ...prev,
      rewards: [...prev.rewards, { place: String(prev.rewards.length + 1), prize: '' }],
    }));
  };

  const removeReward = (idx) => {
    setConfig((prev) => ({
      ...prev,
      rewards: prev.rewards.filter((_, i) => i !== idx),
    }));
  };

  const landingUrl = config.code ? `${window.location.origin}/landing/${config.code}` : '';

  const generatePoster = useCallback((format) => {
    // Backend poster service renders the landing page with Puppeteer and returns PDF
    const posterServiceUrl = import.meta.env.VITE_POSTER_SERVICE_URL || '';
    const code = config.code || 'preview';

    if (posterServiceUrl) {
      // Production: use backend service
      window.open(`${posterServiceUrl}/api/poster/${encodeURIComponent(code)}/${format}`, '_blank');
    } else {
      // Dev fallback: open landing in new window with print dialog (CSS @media print)
      const printWindow = window.open(`/landing/${code}?print=${format}`, '_blank');
      if (printWindow) {
        printWindow.addEventListener('load', () => {
          setTimeout(() => printWindow.print(), 1000);
        });
      }
    }
  }, [config.code]);

  return (
    <Box sx={{ maxWidth: 860, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/campaigns/${id}`)}
          sx={{ borderRadius: `${theme.tokens?.primitive?.radius?.pill ?? 999}px` }}
        >
          Powrót
        </Button>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" sx={{ fontFamily: theme.typography.h1.fontFamily, fontWeight: 750 }}>
            Konfigurator strony zaproszeniowej
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Strona landing zapraszająca pracowników do udziału w kampanii
          </Typography>
        </Box>
      </Box>

      {/* URL bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 2,
          mb: 4,
          borderRadius: `${theme.tokens?.primitive?.radius?.control ?? 12}px`,
          bgcolor: 'background.paper',
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant="body2" sx={{ flexGrow: 1, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {landingUrl}
        </Typography>
        <IconButton size="small" title="Kopiuj link" onClick={handleCopyLink}>
          <ContentCopyIcon fontSize="small" />
        </IconButton>
        <Button
          size="small"
          variant="outlined"
          startIcon={<VisibilityOutlinedIcon />}
          onClick={() => window.open(`/landing/${config.code || 'preview'}`, '_blank')}
          sx={{ borderRadius: `${theme.tokens?.primitive?.radius?.pill ?? 999}px`, flexShrink: 0 }}
        >
          Podgląd
        </Button>
      </Box>

      {/* Branding */}
      <Section title="Branding i wygląd">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <TextField label="Tytuł kampanii" value={config.campaignTitle} onChange={handleChange('campaignTitle')} fullWidth size="small" placeholder="Np. Wyzwanie krokowe" />
          <TextField label="Podtytuł" value={config.subtitle} onChange={handleChange('subtitle')} fullWidth size="small" placeholder="Krótki, motywujący nagłówek" />
          <TextField label="Nazwa firmy (wyświetlana na stronie)" value={config.companyName} onChange={handleChange('companyName')} fullWidth size="small" placeholder="Np. Acme Sp. z o.o." />
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 32, height: 32, borderRadius: 2, bgcolor: config.heroColor }} />
              <TextField label="Kolor główny" value={config.heroColor} onChange={handleChange('heroColor')} size="small" sx={{ width: 130 }} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 32, height: 32, borderRadius: 2, bgcolor: config.accentColor }} />
              <TextField label="Kolor akcentu" value={config.accentColor} onChange={handleChange('accentColor')} size="small" sx={{ width: 130 }} />
            </Box>
          </Box>
          <TextField label="Kod dołączenia" value={config.code} onChange={handleChange('code')} size="small" sx={{ maxWidth: 200 }} />
        </Box>
      </Section>

      {/* Opis */}
      <Section title="Opis wyzwania">
        <TextField
          label="Opis"
          value={config.description}
          onChange={handleChange('description')}
          fullWidth
          multiline
          rows={4}
          size="small"
        />
        {requiresMobileApp && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Ta kampania wymaga aplikacji mobilnej — strona zaproszeniowa automatycznie pokaże znaczki
            integracji z Apple Health i Health Connect.
          </Alert>
        )}
      </Section>

      {/* Kroki */}
      <Section title="Instrukcja krok po kroku">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {config.steps.map((step, idx) => (
            <Box key={idx} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              <Chip label={idx + 1} size="small" sx={{ fontWeight: 700, mt: 1 }} />
              <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <TextField label="Tytuł kroku" value={step.title} onChange={handleStepChange(idx, 'title')} fullWidth size="small" />
                <TextField label="Opis" value={step.description} onChange={handleStepChange(idx, 'description')} fullWidth size="small" multiline rows={2} />
              </Box>
              <IconButton size="small" onClick={() => removeStep(idx)} sx={{ mt: 1 }}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
          <Button size="small" startIcon={<AddIcon />} onClick={addStep} sx={{ alignSelf: 'flex-start', borderRadius: `${theme.tokens?.primitive?.radius?.pill ?? 999}px` }}>
            Dodaj krok
          </Button>
        </Box>
      </Section>

      {/* Cel charytatywny */}
      <Section title="Cel charytatywny">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControlLabel
            control={<Switch checked={config.charityEnabled} onChange={handleSwitch('charityEnabled')} />}
            label="Pokaż sekcję charytatywną"
          />
          {config.charityEnabled && (
            <>
              <TextField label="Tytuł" value={config.charityTitle} onChange={handleChange('charityTitle')} fullWidth size="small" />
              <TextField label="Opis" value={config.charityDescription} onChange={handleChange('charityDescription')} fullWidth size="small" multiline rows={2} />
              <TextField label="Nazwa fundacji" value={config.charityName} onChange={handleChange('charityName')} fullWidth size="small" />
            </>
          )}
        </Box>
      </Section>

      {/* Zasady */}
      <Section title="Zasady gry">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {config.rules.map((rule, idx) => (
            <Box key={idx} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <TextField label="Tytuł zasady" value={rule.title} onChange={handleRuleChange(idx, 'title')} fullWidth size="small" />
                <TextField label="Opis" value={rule.description} onChange={handleRuleChange(idx, 'description')} fullWidth size="small" />
              </Box>
              <IconButton size="small" onClick={() => setConfig((prev) => ({ ...prev, rules: prev.rules.filter((_, i) => i !== idx) }))} sx={{ mt: 1 }}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setConfig((prev) => ({ ...prev, rules: [...prev.rules, { title: '', description: '' }] }))}
            sx={{ alignSelf: 'flex-start', borderRadius: `${theme.tokens?.primitive?.radius?.pill ?? 999}px` }}
          >
            Dodaj zasadę
          </Button>
        </Box>
      </Section>

      {/* Nagrody */}
      <Section title="Nagrody">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {config.rewards.map((reward, idx) => (
            <Box key={idx} sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              <Chip label={`#${reward.place}`} size="small" sx={{ fontWeight: 700 }} />
              <TextField label="Nagroda" value={reward.prize} onChange={handleRewardChange(idx, 'prize')} fullWidth size="small" />
              <IconButton size="small" onClick={() => removeReward(idx)}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
          <Button size="small" startIcon={<AddIcon />} onClick={addReward} sx={{ alignSelf: 'flex-start', borderRadius: `${theme.tokens?.primitive?.radius?.pill ?? 999}px` }}>
            Dodaj nagrodę
          </Button>
        </Box>
      </Section>

      {/* Generuj plakat */}
      <Section title="Plakat do druku">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Wygeneruj plakat PDF z kodem QR i informacjami o wyzwaniu. Wydrukuj i powieś w biurze, kuchni lub na tablicy ogłoszeń.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            onClick={() => generatePoster('a4')}
            sx={{ borderRadius: `${theme.tokens?.primitive?.radius?.pill ?? 999}px` }}
          >
            Pobierz plakat A4
          </Button>
          <Button
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            onClick={() => generatePoster('a3')}
            sx={{ borderRadius: `${theme.tokens?.primitive?.radius?.pill ?? 999}px` }}
          >
            Pobierz plakat A3
          </Button>
        </Box>
      </Section>

      {/* Publikacja */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          alignItems: { sm: 'center' },
          justifyContent: 'space-between',
          p: 3,
          borderRadius: `${theme.tokens?.primitive?.radius?.card ?? 20}px`,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: 'background.paper',
        }}
      >
        <Box>
          <FormControlLabel
            control={<Switch checked={config.published} onChange={handleSwitch('published')} />}
            label="Strona opublikowana"
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 6 }}>
            {config.published ? 'Strona jest widoczna dla pracowników' : 'Strona jest w trybie roboczym — tylko podgląd'}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          sx={{ borderRadius: `${theme.tokens?.primitive?.radius?.pill ?? 999}px`, flexShrink: 0 }}
        >
          {saving ? 'Zapisuję...' : 'Zapisz konfigurację'}
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default LandingConfigurator;
